/**
 * The quote builder — `/countries/:cc/quotes/new`, and `/countries/:cc/sales-orders/new`.
 *
 * =============================================================================
 * ONE SCREEN, TWO MODES, AND THE SECOND ONE IS THE "DIRECT SALES ORDER"
 * =============================================================================
 * `mode="order"` is the owner's ask: a phone order that must not need a quote to be
 * drafted, sent, opened and then confirmed. It is the SAME form, the same priced
 * document and the same `POST /countries/:cc/quotes` — followed immediately by
 * `POST /quotes/:id/confirm`, which the API now accepts from `draft`
 * (`quote-lifecycle.service.ts` §2, OQ-U3-1).
 *
 * A MODE AND NOT A SECOND SCREEN, because there is no second API. Copying this file
 * would duplicate the no-arithmetic rule, the debounce, the B2 patch requirement and the
 * tax-group picker into a place where each could drift, to change a heading and a button.
 *
 * THREE THINGS THE ORDER MODE STATES OUT LOUD, because each is a real cost of the shape:
 *
 *  1. IT CONSUMES A QUOTE NUMBER TOO. The quote is the pricing worksheet and its number
 *     is the audit trail of it. Hiding that would make the QT number in the register
 *     look like somebody's mistake.
 *  2. IT IS TWO REQUESTS, AND THE SECOND ONE CAN FAIL. If the create succeeds and the
 *     confirm is refused (an unconfirmed product name, a lapsed tax rule), a DRAFT QUOTE
 *     EXISTS. The screen says so, names it, and offers to retry the confirm on THAT
 *     quote — never a second create, which would burn a second number and leave two
 *     drafts for one order.
 *  3. IT IS ALWAYS A CRF QUOTE. Confirming a `tally_sales` quote deliberately creates NO
 *     sales order (§B5), so offering the type here would offer a route to a screen that
 *     produces nothing. The radio is replaced by a sentence saying which kind this is.
 *
 * =============================================================================
 * THE ONE RULE THIS SCREEN EXISTS TO OBEY
 * =============================================================================
 * THE CLIENT NEVER COMPUTES MONEY.
 *
 * The form holds strings: which customer, which products, how many, what discount is
 * being asked for. Every NUMBER on the page — each line's `line_total` in its own row,
 * the subtotal, the discount total, the tax and the grand total — arrived in the body
 * of `POST /countries/:cc/quotes/preview`.
 *
 * There is no `qty * rate` in this file. There is no `.reduce()` over line
 * totals. There is no "…and while we wait for the server, show an estimate". If
 * a future edit finds itself needing one of those, the thing it actually needs is
 * another preview call.
 *
 * Two structural properties enforce it rather than asking for it:
 *  · the priced figures are rendered from `preview.data` and from NOTHING ELSE.
 *    The form state cannot reach the totals stack, because its props come off
 *    the response object.
 *  · `useQuotePreview` fires on a debounce and marks its answer STALE while the
 *    on-screen basket is ahead of the priced one. Stale server numbers are still
 *    server numbers; an interpolated fresh number would not be.
 * `quotes-builder.test.tsx` asserts both by handing the component a preview whose
 * arithmetic is deliberately WRONG and proving the screen renders the wrong
 * numbers faithfully. A component that "corrected" them would fail that test,
 * which is the point.
 *
 * =============================================================================
 * WEB TIER A · WT6 — THE HOUSE RESTYLE (forms.html screen 2, owner-approved)
 * =============================================================================
 * The page speaks the write-surface grammar the other Tier A pages speak:
 * `PageHead` (title/sub/actions), numbered `StepCard`s (① Customer, ② Lines),
 * the `ReadyBar` honesty pair, and an aside of `RecordCard`s (Document — the
 * type/tax/validity properties; What-Save-draft-does). Three rulings land here:
 *
 *  · THE SERVER'S LINE TOTALS SIT IN-ROW — each line editor carries the figure
 *    §9.1 computed for THAT line, beside the controls that produced it, or the
 *    NO_VALUE dash while the line does not price. The mapping is positional:
 *    the request sends the usable lines in order and the server answers them
 *    with `line_no` 1..N in that same order.
 *  · THE TOTALS STACK IS THE RECORD'S — the same `components/ui/TotalsBlock`,
 *    the same row labels (Subtotal / Discount signed / the named taxes with
 *    their bases / Tax / Grand total). The preview's extra `taxable_base` is
 *    NO LONGER RENDERED (parity with the record, which has no such column);
 *    the wire still carries it. The W1 stale treatment wraps the stack —
 *    caption, `aria-busy`, reduced opacity — exactly as before.
 *  · THE PRODUCT PICKER IS THE VENDORED COMBOBOX, rendering the catalogue
 *    as-is including its holes: a row whose list price is the seeded
 *    placeholder `0.00` (or masked `•••`) prints the NO_VALUE dash in the
 *    price slot and choosing it PREFILLS NOTHING — a picker that showed ₹0
 *    for an unpriced product would be inventing a price of zero (forms.html
 *    §6, "No invented price"). The U9 shelf select survives unchanged above
 *    it; the native `<optgroup>` headings died with the native select.
 *
 * THE PROSPECT ROUND TRIP (the WT5 contract, honoured here): the customer
 * picker's create row navigates to `parties/new` with `{ returnTo, name,
 * draft }` where draft is THIS builder's whole form state; the page returns
 * with `{ party, draft }` and this component restores both on mount — the
 * customer chosen and every line still in place.
 *
 * =============================================================================
 * "BEHIND" IS TWO STATES, AND THE SCREEN MUST NOT DISTINGUISH THEM (W1)
 * =============================================================================
 * The figure on screen stops describing the form the instant a key is pressed,
 * and starts describing it again only when the SERVER'S ANSWER LANDS. Between
 * those two moments there are two mechanically different states — the 350ms
 * debounce (`preview.isStale`) and the request in flight (`preview.isFetching`) —
 * and to the person reading the total they are the same fact: THIS NUMBER IS NOT
 * YOUR BASKET'S.
 *
 * The first cut of this screen marked only the debounce. Measured on the real
 * API, changing a quantity showed the OLD grand total at full opacity, captioned
 * "Priced by the server", `aria-busy="false"`, with Save ENABLED, for the whole
 * ~5s round trip. That is the exact failure this unit exists to prevent, arriving
 * through the door marked "loading state" instead of the one marked "arithmetic".
 *
 * So there is ONE derived flag, {@link QuoteBuilderPage} computes it once, and
 * everything that could disagree — the opacity, the caption, `aria-busy`, and the
 * Save button — reads that flag. `quotes-builder.test.tsx` proves it by HOLDING a
 * preview response open: the assertions are about the state, not about how fast
 * a stub happens to resolve, which is why the original defect passed a green
 * suite.
 *
 * =============================================================================
 * THE RATE IS THE OPERATOR'S — AND THAT IS NOT THE CLIENT COMPUTING MONEY
 * =============================================================================
 * Phase 3a close-out, T0: OQ-1 is void. CRF is internal-only and is not a pricing
 * engine. The operator already knows what was agreed with the customer, so the
 * line editor has a RATE field and the value goes to the API as `unit_rate`.
 *
 * The rule at the top of this file is unchanged by that, and the distinction is
 * worth stating because it is the whole design: a RATE IS AN INPUT, LIKE A
 * QUANTITY. `qty`, `unit_rate` and `discount_pct` are three things a person types;
 * `line_total`, the subtotal, the discount total, the taxable base, the tax and
 * the grand total are six things §9.1 derives, on the server, and this file still
 * contains no arithmetic that produces any of them. The totals stack still says
 * "Priced by the server" and that sentence is still true.
 *
 * THE FIELD IS PREFILLED FROM THE PRICE LIST, and empty means "use the list".
 * `country_products.price` is the default the API applies when `unit_rate` is
 * absent, so prefilling it makes the screen show what the server is about to do
 * rather than leaving the operator to discover it in the preview. Clearing the
 * field is therefore not an error state — it is the same request the builder sent
 * before this field existed. A PLACEHOLDER price (`0.00`, or masked) prefills
 * nothing — see the WT6 block above.
 */

import { useMemo, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { Plus, Trash2 } from 'lucide-react';
import { useCountryCode } from '../../auth/guards';
import { useSession } from '../../auth/session';
import {
  Alert,
  Button,
  Combobox,
  ConfirmModal,
  PageHead,
  ReadyBar,
  RecordCard,
  SelectField,
  StepCard,
  TextField,
  TotalsBlock,
} from '../../components/ui';
import type { TotalsRowSpec } from '../../components/ui';
import { formatMoney, isMaskedAmount, NO_VALUE, toDateInputValue } from '../inquiries/format';
import { countryProfile } from '../../domain/countries';
import { QUOTE_TYPE_LABELS, deriveQuoteType, missingPatchReason } from '../../domain/quotes';
import { missingSaftFieldsReason } from '../../domain/saft-fields';
import { cn } from '../../lib/cn';
import { ServerAmount, ServerDeduction } from './money';
import { PartyPicker } from './PartyPicker';
import { clockBehindOf, clockBehindSentence, quoteErrorText } from './errors';
import type { ClockBehind } from './errors';
import { MAX_QUOTE_LINES } from './api';
import type {
  PartyDto,
  ProductKind,
  QuoteConfirmationDto,
  QuoteInput,
  QuoteLineInput,
  QuoteRecordDto,
  RecoveryInput,
  TaxGroupDto,
} from './api';
import {
  useCatalogue,
  useConfirmQuoteById,
  useCreateQuote,
  useQuoteCurrencies,
  useQuotePreview,
  useTaxGroups,
} from './use-quotes';
import type { CatalogueEntry } from './use-quotes';

/* ───────────────────────────────────────────────────────────── draft state ── */

/**
 * One line as the FORM holds it: strings, exactly what the operator typed.
 *
 * `key` is a client-side identity for React and for nothing else. `quote_lines`
 * rows carry no client-addressable identity — the client sends products, and
 * `line_no` is a position in the array (`dto/quotes.dto.ts`) — so this key is
 * never sent and never read back.
 */
interface DraftLine {
  key: string;
  product_id: string;
  qty: string;
  /**
   * DECIMAL(14,2) as typed. Prefilled from the price list when a product is
   * chosen; BLANK means "let the server use the list", which is the request this
   * builder sent before the rate was an input at all.
   */
  unit_rate: string;
  discount_pct: string;
  users: string;
  months: string;
  description: string;
  /**
   * Renewal term in years, as typed — '1' or '2', for a term-bearing product.
   *
   * BLANK means "let the server prefill", exactly like `unit_rate` above. A string and
   * not a number because every field on this draft is a string: the input's value IS the
   * state, and converting on the way in would make an in-progress edit unrepresentable.
   */
  term_years: string;
  /**
   * The FIRST day cover runs, 'YYYY-MM-DD'. BLANK means "let the server prefill it".
   *
   * ⚠ 5.2 SHIPPED THE API AND NOT THIS FIELD, AND A HALF-BUILT FEATURE IS THE WORSE
   * FAILURE. The column, the DTO, the pricing and the sales-order snapshot have preserved
   * `term_start` since migration 25 — but with no input, every start date in the system was
   * whatever the server prefilled, and the case the column exists for (a renewal sold in
   * November to start on 1 January, when the previous cover ends) was unreachable through
   * the UI. The operator can now state it.
   */
  term_start: string;
  /**
   * The last day cover runs, 'YYYY-MM-DD'. BLANK means "let the server derive it".
   *
   * ⚠ THE CLIENT NEVER COMPUTES THIS. The server prefills from the sale date and the
   * term, and the operator may type anything — a renewal offer giving thirteen months
   * for twelve is a real case and the whole reason the field is editable. A helpful
   * client-side recomputation would overwrite exactly those.
   */
  expiry_date: string;
}

let nextLineKey = 1;

/** A fresh client-side key — also used to RE-KEY lines restored from the
 * parties/new round trip, so a restored draft can never collide with keys this
 * mount issues afterwards. */
function freshLineKey(): string {
  const key = `line-${String(nextLineKey)}`;
  nextLineKey += 1;
  return key;
}

function emptyLine(): DraftLine {
  return {
    key: freshLineKey(),
    product_id: '',
    qty: '1',
    unit_rate: '',
    discount_pct: '',
    users: '',
    months: '',
    description: '',
    term_years: '',
    term_start: '',
    expiry_date: '',
  };
}

/** `qty` is DECIMAL(10,2); the API REJECTS over-scale rather than rounding it. */
const QTY_PATTERN = /^\d{1,8}(\.\d{1,2})?$/;
/** `discount_pct` is DECIMAL(6,3). */
const PCT_PATTERN = /^\d{1,3}(\.\d{1,3})?$/;
/**
 * `unit_rate` is DECIMAL(14,2) — twelve integer digits and two decimals.
 *
 * The pattern is a COURTESY, not the rule. `assertDecimalString` on the server is
 * the rule, and it refuses an over-scale literal outright rather than letting
 * MySQL round 1234.565 into 1234.57. Checking the same thing here means the
 * operator is told at the keystroke instead of at the preview.
 */
const RATE_PATTERN = /^\d{1,12}(\.\d{1,2})?$/;

function qtyError(value: string): string | null {
  const trimmed = value.trim();
  if (trimmed === '') return 'A quantity is required.';
  if (!QTY_PATTERN.test(trimmed)) return 'Digits with at most two decimals, e.g. 1 or 2.50';
  if (Number(trimmed) <= 0) return 'Must be more than zero.';
  return null;
}

/**
 * The raw-`select` class string.
 *
 * DUPLICATED FROM `features/admin/overrides-editor.tsx`, deliberately and with the reason
 * recorded: there is no `Select` primitive in `components/ui`, and the two live selects in
 * this app both spell it inline. Extracting a component for the second usage would be the
 * right move at the third; copying one line and saying so beats importing across feature
 * folders for a string.
 */
const SELECT_CLASSES =
  'h-9 max-w-full rounded-sm border border-line bg-subtle px-2.5 text-body text-fg outline-none';

/**
 * A date the operator typed, or blank.
 *
 * ⚠ RENAMED FROM `expiryError` IN U3.1 — it now guards BOTH ends of the term, and a name
 * saying "expiry" on the start field would be the kind of small lie that makes the next
 * reader add a second, subtly different validator.
 *
 * Blank is NOT an error: it is how the operator asks the server to prefill. Shape only —
 * it does NOT compare the value against the term or the sale date. The owner's ruling is
 * that the operator's date is the truth (thirteen months for twelve is a real offer), so
 * a client that "corrected" a date would break exactly the cases the field exists for.
 */
function dateError(value: string): string | null {
  const trimmed = value.trim();
  if (trimmed === '') return null;
  if (!/^\d{4}-\d{2}-\d{2}$/.test(trimmed)) return 'Use YYYY-MM-DD.';
  // Round-trips only if the date exists: '2026-02-30' normalises to March and fails.
  const at = new Date(`${trimmed}T00:00:00.000Z`);
  if (Number.isNaN(at.getTime()) || at.toISOString().slice(0, 10) !== trimmed) {
    return 'That date does not exist.';
  }
  return null;
}

function rateError(value: string, pricedPerCustomer = false): string | null {
  const trimmed = value.trim();
  // Empty is legitimate and is not an error: the server falls back to the price
  // list. See the file header.
  //
  // EXCEPT for a product PRICED PER CUSTOMER (owner ruling R3, 2026-09-22 — the government
  // patch): its list price is deliberately blank, so there is nothing to fall back to and
  // the salesperson must type it. Blank and zero are both refused here, at the keystroke,
  // in the same words the server would use at the preview.
  if (trimmed === '') return pricedPerCustomer ? "Type this customer's price." : null;
  if (!RATE_PATTERN.test(trimmed)) return 'Digits with at most two decimals, e.g. 18000 or 999.50';
  if (pricedPerCustomer && Number(trimmed) <= 0) return 'Must be more than zero.';
  return null;
}

/**
 * The line's product is priced per customer (R3) — its list row exists and carries no price.
 * Read off the catalogue flag, never off `price === null`: a masked price is null too.
 */
function pricedPerCustomer(entry: CatalogueEntry | null | undefined): boolean {
  return entry !== undefined && entry !== null && entry.priced_per_customer;
}

/** Plain names for the currencies CRF quotes in — a label beside the code, never a rule. */
const CURRENCY_NAMES: Readonly<Record<string, string>> = {
  CDF: 'Congolese franc',
  USD: 'US dollar',
  INR: 'Indian rupee',
  AED: 'UAE dirham',
  TZS: 'Tanzanian shilling',
  AOA: 'Angolan kwanza',
};

function currencyLabel(code: string): string {
  const name = CURRENCY_NAMES[code];
  return name === undefined ? code : `${name} (${code})`;
}

function pctError(value: string): string | null {
  const trimmed = value.trim();
  if (trimmed === '') return null;
  if (!PCT_PATTERN.test(trimmed)) return 'Digits with at most three decimals, e.g. 5 or 7.5';
  if (Number(trimmed) > 100) return 'A discount cannot exceed 100%.';
  return null;
}

/** A line the request can carry — the SAME predicate `toRequest` filters by,
 * extracted so the in-row line-total mapping cannot use a different one.
 *
 * `perCustomer` is the line's catalogue flag (R3): a priced-per-customer line without a
 * typed price above zero is not askable, exactly as a line with a malformed quantity is not,
 * and Save names it (`blockedReason`). */
function usableLine(line: DraftLine, perCustomer = false): boolean {
  return (
    line.product_id !== '' &&
    qtyError(line.qty) === null &&
    rateError(line.unit_rate, perCustomer) === null &&
    pctError(line.discount_pct) === null
  );
}

/**
 * A list price that IS a price — or `null` for the catalogue's holes.
 *
 * The seeded placeholder `0.00` and a masked `•••` are not prices: the picker
 * prints the NO_VALUE dash for them, and choosing such a row prefills NOTHING
 * (forms.html §6, "No invented price" — a picker that showed ₹0 for an unpriced
 * product would be inventing a price of zero, and a prefill would go further and
 * SEND it, making the line free).
 */
function listPriceOf(entry: CatalogueEntry | null | undefined): string | null {
  if (entry === undefined || entry === null) return null;
  // A blank list price — priced per customer (R3) — is no price either, and prefills nothing.
  if (entry.price === null) return null;
  const price = entry.price.trim();
  if (price === '' || isMaskedAmount(price)) return null;
  if (/^0+(\.0+)?$/.test(price)) return null;
  return price;
}

/**
 * The form's lines as the API's line inputs — or `null` when the basket is not
 * yet askable.
 *
 * NOTE WHAT IS ABSENT: no `line_total`, and no total of any kind. `unit_rate` is
 * sent because the operator typed it; every DERIVED figure is missing, the type
 * (`QuoteLineInput`) has no field for one, and the global `ValidationPipe` runs
 * with `forbidNonWhitelisted`, so even a hand-edited body would be refused rather
 * than silently stripped.
 */
function toRequest(
  currency: string | null,
  party: PartyDto | null,
  lines: readonly DraftLine[],
  validTill: string,
  taxGroupId: string,
  isUsable: (line: DraftLine) => boolean,
): QuoteInput | null {
  // No currency, no request (2026-09-22): in a two-currency country nothing is priced until
  // the salesperson has chosen, and in a one-currency country it is known once the country's
  // currencies have loaded.
  if (currency === null) return null;
  if (party === null) return null;
  const usable = lines.filter(isUsable);
  if (usable.length === 0) return null;
  return {
    /*
     * ⚠ NO `type` — REMOVED IN C11/IQ-12b, ON BOTH ROUTES, AND THE ABSENCE IS THE POINT.
     *
     * It used to read `type: quoteType`, with the note that it was *"sent on BOTH preview
     * and create … It changes no number on preview; it decides what the saved document
     * IS."* That second half stopped being true: `quotes.type` is DERIVED from the lines
     * by the server (owner rulings R1/Q3), so what decides the document is the basket
     * below, and this page reads the answer rather than stating it.
     *
     * Sending it anyway would be pointless: the server checks a stated type against the
     * derived one and the claim never survives into storage either way.
     *
     * ⚠ THE CONSEQUENCE OF SENDING IT CHANGED, AND THIS COMMENT STATED THE OLD ONE. It
     * read *"worse than pointless — … answers 422 when they disagree (D-IQ-1), so a stale
     * client belief would surface as a refused save on a perfectly legal document"*. That
     * refused save is now what the STALE-CLIENT TOLERANCE exists to prevent: owner ruling
     * 1, `apps/api/src/common/client-compat.ts`, default ON, under which a disagreeing
     * claim is ignored and the save proceeds on the derived type. The 422 returns only
     * with `CRF_STALE_CLIENT_TOLERANCE=0`.
     *
     * ⚠ AND THE CLIENT BEING TOLERATED IS THIS FILE AT `ef68f2c`, WHERE THIS BLOCK STILL
     * READ `type: quoteType` — measured at `ef68f2c:…/QuoteBuilderPage.tsx:391`. Removing
     * it here does not remove it from the browsers already holding that bundle, which is
     * the entire reason the API bends. Do not read the tolerance as permission to send it
     * again: this page is the NEW client, and a new client sending a field the tolerance
     * was built to retire would keep the tolerance alive on its own evidence.
     */
    party_id: party.id,
    /*
     * THE CHOSEN CURRENCY — on BOTH routes, always (owner, 2026-09-22: *"before generating
     * any quote for CD user has to select the option in which currency they are to create
     * quote in"*). Every line is priced from this currency's list and nothing is converted.
     *
     * ⚠ RULE-5 CALLOUT: this body used to carry NO `currency` (decision U1-1 — derived from
     * the price list), and `quotes-builder.test.tsx` pinned the absence. The pin is inverted
     * with the same callout there.
     */
    currency,
    // Ignored by `preview` (it changes no number) and stored by `create`. Sent to
    // both because ONE DTO serves both routes: the body that priced cleanly must
    // be the body that saves, and `forbidNonWhitelisted` makes a divergence a 400.
    ...(validTill.trim() === '' ? {} : { valid_till: validTill.trim() }),
    // OMITTED when the operator left "Country default" selected — absence is what
    // asks the server for the default group, the same omit-when-blank rule as
    // `unit_rate`. Sent on BOTH routes so the body that priced is the body that
    // saves (migration 30, OD-1 — design U3).
    ...(taxGroupId === '' ? {} : { tax_group_id: taxGroupId }),
    lines: usable.map((line): QuoteLineInput => {
      const input: QuoteLineInput = { product_id: line.product_id, qty: line.qty.trim() };
      // Omitted when blank, never sent as '' or 0 — absence is what asks the
      // server for the price list, and a zero would be a free line.
      if (line.unit_rate.trim() !== '') input.unit_rate = line.unit_rate.trim();
      if (line.discount_pct.trim() !== '') input.discount_pct = line.discount_pct.trim();
      if (line.users.trim() !== '') input.users = Number(line.users.trim());
      if (line.months.trim() !== '') input.months = Number(line.months.trim());
      if (line.description.trim() !== '') input.description = line.description.trim();
      // Same omit-when-blank rule as the rate: absence asks the server to prefill, and
      // sending '' or 0 would be a claim rather than a question.
      if (line.term_years.trim() !== '') input.term_years = Number(line.term_years.trim());
      /*
       * ⚠ THE TWO ENDS ARE SENT INDEPENDENTLY, each omitted when blank. A stated start with
       * a blank end must reach the server as exactly that, so the server's own independent
       * prefill runs on the end alone — which is the behaviour `quotes.spec.ts`'s
       * `overrides the two ends INDEPENDENTLY` case pins. Pairing them here, or filling one
       * from the other, would make that untrue from the client side only.
       */
      if (line.term_start.trim() !== '') input.term_start = line.term_start.trim();
      if (line.expiry_date.trim() !== '') input.expiry_date = line.expiry_date.trim();
      return input;
    }),
  };
}

/* ────────────────────────────────────────────────────────────────── screen ── */

/**
 * `quote` builds a draft and stops. `order` builds the same draft and confirms it,
 * landing on the sales order. See the file header.
 */
export type BuilderMode = 'quote' | 'order';

/** The parties/new round-trip cargo — the WT5 contract's `draft` half, as THIS
 * page spells it. Restored verbatim on return (lines re-keyed only). */
interface BuilderDraft {
  lines: readonly DraftLine[];
  validTill: string;
  /**
   * The chosen quote currency (2026-09-22) — carried so a salesperson who leaves to create a
   * customer comes back with the currency still chosen and the lines still priced in it.
   * Optional so a hand-off written by an older bundle still restores.
   */
  currency?: string | null;
  /*
   * ⚠ `quoteType` IS GONE FROM THE ROUND-TRIP CARGO (C11/IQ-12b), and nothing replaces
   * it. The parties/new hand-off exists to keep "this page still filled in" true across a
   * navigation, and the type is no longer something the page holds: it is recomputed from
   * `lines` on every render. Carrying it would preserve a stale copy of a derived value.
   */
  taxGroupId: string;
}

interface BuilderHandOff {
  party?: PartyDto;
  draft?: BuilderDraft;
}

/* Tone pills — the forms.html `tone()` chips on the card headers. Tint + word,
 * never colour alone — the same vocabulary InquiryCreatePage carries. */
function OkPill({ children }: { children: string }): React.JSX.Element {
  return (
    <span className="inline-flex items-center rounded-pill bg-ok-bg px-2.5 py-0.5 text-label font-medium text-ok-fg">
      {children}
    </span>
  );
}
function InfoPill({ children }: { children: string }): React.JSX.Element {
  return (
    <span className="inline-flex items-center rounded-pill bg-info-bg px-2.5 py-0.5 text-label font-medium text-info-fg">
      {children}
    </span>
  );
}
function NeutralPill({ children }: { children: string }): React.JSX.Element {
  return (
    <span className="inline-flex items-center rounded-pill bg-subtle px-2.5 py-0.5 text-label font-medium text-fg-secondary ring-1 ring-line">
      {children}
    </span>
  );
}

export function QuoteBuilderPage({ mode = 'quote' }: { mode?: BuilderMode } = {}): React.JSX.Element {
  const countryCode = useCountryCode();
  const { can } = useSession();
  const navigate = useNavigate();
  const location = useLocation();

  const isOrder = mode === 'order';
  const canCreate = can(countryCode, 'quotes.create');
  const canConfirm = can(countryCode, 'quotes.confirm');
  const canSeeOrders = can(countryCode, 'so.view');
  const canSeeQuotes = can(countryCode, 'quotes.view');
  const canSeeAmounts = can(countryCode, 'amounts.view');

  /** The parties/new round trip landing (header, WT6 block): the created
   * customer arrives as `party`, the whole form as `draft`. */
  const handOff = (location.state ?? null) as BuilderHandOff | null;

  const [party, setParty] = useState<PartyDto | null>(handOff?.party ?? null);
  const [lines, setLines] = useState<readonly DraftLine[]>(() => {
    const restored = handOff?.draft?.lines;
    if (restored === undefined || restored.length === 0) return [emptyLine()];
    // Re-keyed, never trusted: the incoming keys were issued by ANOTHER mount's
    // counter and could collide with the ones this mount issues next.
    return restored.map((line) => ({ ...line, key: freshLineKey() }));
  });
  const [validTill, setValidTill] = useState(handOff?.draft?.validTill ?? '');
  /**
   * ORDER MODE ONLY — the quote that was SAVED but not yet confirmed.
   *
   * Non-null means the first request succeeded and the second did not, which is a real
   * state with a real row behind it: a draft quote holding a document number. It is held
   * so the retry confirms THAT quote instead of creating another one.
   */
  const [savedDraft, setSavedDraft] = useState<QuoteRecordDto | null>(null);
  /** The confirm's own refusal text, kept apart from the create's. */
  const [confirmError, setConfirmError] = useState<unknown>(null);
  /** DE 74/19 33 c) (AGT U2): the confirm was refused on the server's clock — ask the operator. */
  const [clockRetry, setClockRetry] = useState<ClockBehind | null>(null);
  /** Set when the order was created and this account may not open it (`so.view`). */
  const [placedOrder, setPlacedOrder] = useState<QuoteConfirmationDto | null>(null);
  /**
   * WHICH NAMED TAX CONFIGURATION prices this document (migration 30, OD-1 — U3).
   * '' means "the country default", which is a real choice and the resting state:
   * the request then carries no `tax_group_id` and the server resolves the default,
   * exactly as every create priced before the picker existed.
   */
  const [taxGroupId, setTaxGroupId] = useState(handOff?.draft?.taxGroupId ?? '');
  /**
   * THE QUOTE CURRENCY, CHOSEN BEFORE THE FIRST LINE (owner, 2026-09-22: *"before generating
   * any quote for CD user has to select the option in which currency they are to create
   * quote in"*). `null` until chosen — nothing is preselected — and only ever asked in a
   * country that quotes in more than one (CD: CDF or USD). A one-currency country never
   * shows the step and uses its one currency.
   */
  const [chosenCurrency, setChosenCurrency] = useState<string | null>(
    handOff?.draft?.currency ?? null,
  );
  /** A change of currency waiting on the salesperson's word, because it clears the lines. */
  const [pendingCurrency, setPendingCurrency] = useState<string | null>(null);
  /**
   * AGT U7 — A RECOVERY DOCUMENT: a quotation written by hand during an outage (`M`) or the
   * duplicate of one lost in a restore (`D`). Offered in a SAF-T(AO) country to config.manage
   * only; `''` is an ordinary draft. The server refuses outside the AGT regime (the page cannot
   * tell whether the série is open) and checks the reference's shape and the date. The original's
   * identifiers are SEPARATE fields (8 c), 9 f): "por forma a evitar lapsos"), a D's type code too.
   */
  const canRecover = !isOrder && countryProfile(countryCode).saftAo && can(countryCode, 'config.manage');
  const [recoveryKind, setRecoveryKind] = useState<'' | 'M' | 'D'>('');
  const [recoveryTypeCode, setRecoveryTypeCode] = useState('');
  const [recoverySerie, setRecoverySerie] = useState('');
  const [recoveryNumber, setRecoveryNumber] = useState('');
  const [recoveryDate, setRecoveryDate] = useState('');
  const recovery: RecoveryInput | null =
    canRecover && recoveryKind !== ''
      ? {
          recovery_kind: recoveryKind,
          ...(recoveryKind === 'D' ? { recovery_type_code: recoveryTypeCode.trim() } : {}),
          recovery_serie: recoverySerie.trim(),
          recovery_number: recoveryNumber.trim(),
          recovery_date: recoveryDate,
        }
      : null;

  const currencies = useQuoteCurrencies(countryCode, canCreate);
  const choiceRequired = currencies.data?.choice_required === true;
  /** The currency this quote is in — the choice, or the country's only one; null while unknown. */
  const currency: string | null =
    currencies.data === undefined
      ? null
      : choiceRequired
        ? chosenCurrency !== null && currencies.data.currencies.includes(chosenCurrency)
          ? chosenCurrency
          : null
        : (currencies.data.currencies[0] ?? null);

  const catalogue = useCatalogue(countryCode, currency, canCreate);
  const taxGroups = useTaxGroups(countryCode, canCreate);
  const catalogueById = useMemo(
    () => new Map((catalogue.data ?? []).map((entry) => [entry.product_id, entry])),
    [catalogue.data],
  );
  /** `usableLine` with the line's own catalogue flag — ONE predicate for request and totals. */
  const isUsable = (line: DraftLine): boolean =>
    usableLine(line, pricedPerCustomer(catalogueById.get(line.product_id)));

  const request = useMemo(
    () =>
      toRequest(currency, party, lines, validTill, taxGroupId, (line) =>
        usableLine(line, pricedPerCustomer(catalogueById.get(line.product_id))),
      ),
    [currency, party, lines, validTill, taxGroupId, catalogueById],
  );
  const preview = useQuotePreview(countryCode, request, canCreate);
  const create = useCreateQuote(countryCode);
  const confirm = useConfirmQuoteById(countryCode);

  /**
   * THE FIGURES ON SCREEN ARE NOT THIS BASKET'S. See the file header.
   *
   * `isStale` alone is the debounce; `isFetching` alone would miss the keystrokes
   * before the timer settles. The union is the honest window, and it is computed
   * HERE — once — so the document's treatment and the Save button cannot come to
   * different conclusions about the same moment.
   */
  const previewBehind = preview.isStale || preview.isFetching;

  /*
   * ORDER MODE NEEDS BOTH KEYS, because it performs both acts. `quotes.create` alone
   * cannot finish the job, and a screen that let somebody build a whole basket and then
   * refused the last click would be worse than not offering it — the entry point on the
   * sales-order list applies the same pair, so this is the second lock on the same door
   * (a typed URL).
   */
  if (!canCreate || (isOrder && !canConfirm)) {
    return (
      <div className="flex flex-col gap-4 p-6">
        <Alert
          tone="danger"
          title={
            isOrder
              ? 'Sales orders cannot be raised from this account'
              : 'Quotes cannot be raised from this account'
          }
        >
          {isOrder
            ? `Raising an order directly in ${countryCode} needs BOTH the Quotes permission and ` +
              'the Confirm permission — it writes a quote and confirms it in one step. Ask an ' +
              'administrator to widen your access.'
            : `Building a quote in ${countryCode} needs the Quotes permission. Ask an administrator ` +
              'to widen your access.'}
        </Alert>
        <Link
          to={`/countries/${countryCode}/${isOrder ? 'sales-orders' : 'quotes'}`}
          className="w-fit text-body13 text-fg underline underline-offset-2 hover:decoration-2"
        >
          {isOrder ? 'Back to the sales-order list' : 'Back to the quote list'}
        </Link>
      </div>
    );
  }

  const patchLine = (key: string, patch: Partial<DraftLine>): void => {
    setLines((current) =>
      current.map((line) => (line.key === key ? { ...line, ...patch } : line)),
    );
  };

  /**
   * Choosing a product PREFILLS the rate from that product's country price.
   *
   * It is a prefill and not a lock: the field stays editable, and clearing it
   * asks the server for the list price anyway. Showing the number the server
   * would have used is the point — the operator either accepts it or types what
   * was actually agreed, and either way nothing about the rate is a surprise
   * discovered in the preview.
   *
   * ⚠ A PLACEHOLDER PRICE PREFILLS NOTHING (WT6, `listPriceOf`): the seeded
   * `0.00` is not a price and a masked `•••` is not even a number — copying
   * either into the rate field would COMMIT it on the wire (a free line, or a
   * keystroke-refused literal). The dash rows never commit a price; the field
   * stays blank, which is the honest "ask the server for the list".
   */
  const chooseProduct = (key: string, productId: string): void => {
    patchLine(key, {
      product_id: productId,
      unit_rate: listPriceOf(catalogueById.get(productId)) ?? '',
    });
  };

  const addLine = (): void => {
    setLines((current) => (current.length >= MAX_QUOTE_LINES ? current : [...current, emptyLine()]));
  };

  const removeLine = (key: string): void => {
    setLines((current) => {
      const next = current.filter((line) => line.key !== key);
      // Never zero rows: an empty line editor with an "Add line" button reads as a
      // broken screen. One blank row is the resting state.
      return next.length === 0 ? [emptyLine()] : next;
    });
  };

  /** The parties/new hand-off (header): leave with the WHOLE form as the draft,
   * so "this page still filled in" is keepable on the way back. */
  const handOffToPartyCreate = (typedName: string): void => {
    navigate(`/countries/${countryCode}/parties/new`, {
      state: {
        returnTo: location.pathname,
        name: typedName,
        draft: { lines, validTill, taxGroupId, currency: chosenCurrency } satisfies BuilderDraft,
      },
    });
  };

  /**
   * Choosing the currency. The first choice simply sets it. A CHANGE after products are on
   * the lines waits for the salesperson's word, because it clears them: a rate typed in USD
   * is not a CDF rate, and nothing is ever converted (owner, 2026-09-22). The customer stays.
   */
  const chooseCurrency = (code: string): void => {
    if (code === chosenCurrency) {
      setPendingCurrency(null);
      return;
    }
    const hasProducts = lines.some((line) => line.product_id !== '');
    if (chosenCurrency !== null && hasProducts) {
      setPendingCurrency(code);
      return;
    }
    setChosenCurrency(code);
    setPendingCurrency(null);
  };
  const confirmCurrencyChange = (): void => {
    if (pendingCurrency === null) return;
    setChosenCurrency(pendingCurrency);
    setLines([emptyLine()]);
    setPendingCurrency(null);
  };

  const save = (confirmClock = false): void => {
    if (request === null) return;
    create.mutate(
      recovery === null ? request : { ...request, ...recovery, ...(confirmClock ? { confirm_clock: true } : {}) },
      {
        onSuccess: (record) => {
          setClockRetry(null);
          navigate(`/countries/${countryCode}/quotes/${record.id}`);
        },
        // 33 c) (AGT U2): a recovery document is frozen as it is saved, so the clock question is asked here.
        onError: (error) => {
          if (recovery !== null) setClockRetry(clockBehindOf(error));
        },
      },
    );
  };

  /**
   * ORDER MODE — confirm a quote that already exists, and land on the order.
   *
   * Split out from {@link placeOrder} because it is also the RETRY path: when the create
   * succeeded and the confirm did not, this runs again against the SAME `quoteId`. A
   * retry that re-ran the create would consume a second QT number and leave an orphan
   * draft behind for every attempt.
   */
  const confirmInto = (quoteId: string, confirmClock = false): void => {
    setConfirmError(null);
    confirm.mutate({ quoteId, confirmClock }, {
      onSuccess: (result) => {
        setSavedDraft(null);
        setClockRetry(null);
        /*
         * THE SERVER DECIDES WHETHER AN ORDER EXISTS, not this screen. `sales_order` is
         * null for a `tally_sales` confirm (§B5) — unreachable from here, because order
         * mode fixes the type — and branching on the RESPONSE rather than on what this
         * client believes means the operator is told the truth either way.
         */
        if (result.sales_order !== null && canSeeOrders) {
          navigate(`/countries/${countryCode}/sales-orders/${result.sales_order.id}`);
          return;
        }
        // No `so.view` (or, impossibly, no order): stay, and state what exists rather
        // than navigating to a screen that would refuse this account.
        setPlacedOrder(result);
      },
      onError: (error) => {
        setConfirmError(error);
        // 33 c) (AGT U2): the server's clock is behind the last document — ask, don't just fail.
        setClockRetry(clockBehindOf(error));
      },
    });
  };

  /** ORDER MODE — create the quote, then confirm it. Two requests, stated as two. */
  const placeOrder = (): void => {
    if (request === null) return;
    setConfirmError(null);
    create.mutate(request, {
      onSuccess: (record) => {
        // Held BEFORE the confirm is attempted: if the confirm throws, this is the row
        // the operator now owns and the screen has to be able to name it.
        setSavedDraft(record);
        confirmInto(record.id);
      },
    });
  };

  /*
   * §B2 made VISIBLE. The chosen lines' kinds and the country's patch products both
   * come from the catalogue this page already loads, so the requirement can be stated
   * while the operator is still building the basket.
   */
  const chosenKinds: string[] = lines
    .map((line) => catalogueById.get(line.product_id)?.kind)
    .filter((kind): kind is ProductKind => kind !== undefined);
  const patchesOnSale = (catalogue.data ?? [])
    .filter((entry) => entry.kind === 'gov_patch' && entry.active)
    .map((entry) => entry.product_code)
    .sort();
  /*
   * ═══════════════════════════════════════════════════════════════════════════
   * THE DOCUMENT'S KIND, COMPUTED — not chosen (owner rulings R1/Q3).
   * ═══════════════════════════════════════════════════════════════════════════
   * The same rule the server runs (`deriveQuoteType`), over the same input this page
   * already had for the patch requirement: the `kind` of every chosen line.
   *
   * ⚠ IT IS A DISPLAY, NOT A DECISION. The request carries no type at all, so nothing
   * here can put a wrong value on a document — the worst a divergence could do is show
   * the operator a label the server disagrees with, and the label is re-read from the
   * saved record the moment the quote exists. The mirror is kept honest by
   * `quotes.test.ts`, which pins this function against the server's own source.
   *
   * ⚠ AND A LINE WHOSE PRODUCT IS NOT YET PICKED CONTRIBUTES NO KIND, which is why an
   * empty basket reads `CRF customisation`: a document that sells no Tally licence is a
   * CRF quote, and that is the honest answer rather than a blank.
   */
  const derivedType = deriveQuoteType(chosenKinds);
  const patchReason = missingPatchReason(
    countryCode,
    derivedType,
    chosenKinds,
    patchesOnSale,
    choiceRequired ? currency : null,
  );

  /*
   * PRICED PER CUSTOMER (R3): a chosen line whose list price is blank and whose rate is not
   * yet a price above zero. Named by product, so the salesperson knows which box to fill.
   */
  const needsCustomerPrice = lines
    .map((line) => catalogueById.get(line.product_id))
    .filter((entry, index): entry is CatalogueEntry =>
      pricedPerCustomer(entry) && rateError(lines[index]?.unit_rate ?? '', true) !== null,
    );
  const customerPriceReason =
    needsCustomerPrice.length === 0
      ? null
      : `Type this customer's price for ${[
          ...new Set(needsCustomerPrice.map((entry) => entry.product_name)),
        ].join(', ')}.`;

  /*
   * THE SAF-T(AO) MANDATORY FIELDS, MADE VISIBLE (design U8 item 33 a),
   * `docs/ANGOLA-AGT-VALIDATION-DESIGN.md` §3; owner "go" 2026-09-25): "each a named refusal
   * the builder shows before send/confirm/print". The server refuses at the issuance doors
   * with `SAFT_FIELD_MISSING`; this rung mirrors the two rules the builder can see — the
   * party's NIF / address / city and a description over 200 characters — over the party and
   * lines already on this page, keyed on the country PROFILE's `saftAo` (the mirror of the
   * row flag), so the operator reads the sentence while the customer is still being chosen
   * rather than after the document is refused. `domain/saft-fields.ts` carries the words and
   * is held to the API's constants by its test.
   */
  const saftReason = missingSaftFieldsReason(countryCode, party, lines);

  /**
   * Saving is blocked while the document does not price.
   *
   * DISABLED, not absent — and the difference is the D5 rule read correctly. A
   * PERMISSION the caller does not hold is hidden, because showing it would tell
   * them the app is broken for them. This is not a permission: the caller may
   * absolutely create quotes, and this particular basket does not price yet. The
   * button therefore stays, disabled, with the reason beside it — because the
   * reason is actionable and hiding the control would leave them wondering where
   * Save went.
   */
  const blockedReason =
    // THE CURRENCY RUNGS COME FIRST (2026-09-22): nothing below can be judged in a list that
    // has not been chosen.
    // While the currencies load, the request is null and the rung below says what to do next
    // — the same sentence a one-currency country always showed first.
    currencies.isError
      ? 'The currencies this country quotes in could not be loaded. Reload the page.'
      : choiceRequired && currency === null
        ? 'Choose the currency this quote is in.'
        : customerPriceReason !== null
            ? customerPriceReason
            : request === null
              ? 'Pick a customer and at least one product.'
              : patchReason !== null
                ? patchReason
                : saftReason !== null
                  ? // Design U8 33 a) — after the patch rule (a licence condition outranks a
                    // file field) and before the server is asked to price: the fields are
                    // the operator's to fill, and the server would refuse the send anyway.
                    saftReason
                  : recovery !== null &&
                      [recovery.recovery_type_code, recovery.recovery_serie, recovery.recovery_number, recovery.recovery_date].includes('')
                  ? // AGT U7: a recovery document carries its original's identifiers and date.
                    `Type the original document's ${recovery.recovery_kind === 'D' ? 'type code, ' : ''}série, number and date.`
                  : preview.isError
                  ? 'The server could not price this document. Fix the lines below.'
                  : preview.data === undefined
                    ? 'Waiting for the server to price this document.'
                    : previewBehind
                      ? // W1. Save posts the basket in the FORM; the figures on screen belong to
                        // an older one. Letting it through would have the operator approve a
                        // number and the server store a different one — which is the same lie as
                        // computing the total here, told a different way.
                        'Waiting for the server to price the basket on screen.'
                      : choiceRequired && preview.data.currency !== currency
                        ? // Cannot happen by construction — the request names the currency and
                          // the server prices from that list or refuses. Kept, where there is a
                          // choice to get wrong, so it could never save silently if it did.
                          `The server priced this in ${preview.data.currency}, not ${String(currency)}. Reload the page.`
                        : null;

  const busy = create.isPending || confirm.isPending;

  /*
   * THE READY SENTENCE. The bar restates `blockedReason` — with ONE exception:
   * the §B2 patch requirement renders as a danger Alert inside the Lines card
   * (the owner's ruling: a licence condition reads as REQUIRED, once), and
   * repeating its sentence here at label weight would be the two-weights defect
   * the ruling named. The bar points at the card instead.
   */
  const pricedCount = preview.data?.lines.length ?? 0;
  const readyStatement =
    blockedReason === null
      ? `Ready to ${
          isOrder ? 'place the order' : recovery !== null ? 'issue the recovery document' : 'save as a draft'
        }. ${String(pricedCount)} ${
          pricedCount === 1 ? 'line is' : 'lines are'
        } priced by the server.`
      : blockedReason === patchReason
        ? 'A required line is missing — the Lines card names it.'
        : blockedReason;

  /* The in-row line totals' positional mapping — see the header's WT6 block. */
  const usableKeys = lines.filter(isUsable).map((line) => line.key);
  const pricedTotalFor = (key: string): string | null => {
    if (preview.data === undefined) return null;
    const index = usableKeys.indexOf(key);
    if (index < 0) return null;
    return preview.data.lines[index]?.line_total ?? null;
  };

  const document = preview.data;
  const hasRequest = request !== null;

  /** The record's stack, row for row (`QuoteRecordPage.totalsRows`): Subtotal /
   * Discount (signed) / the named taxes with their bases / Tax / Grand total.
   * `taxable_base` is on the preview wire and deliberately NOT a row — parity
   * with the record, which has no such column (WT6 header). */
  const totalsRows: TotalsRowSpec[] =
    document === undefined
      ? []
      : [
          {
            key: 'subtotal',
            label: 'Subtotal',
            value: <ServerAmount value={document.totals.subtotal} currency={document.currency} />,
          },
          {
            key: 'discount',
            label: 'Discount',
            // SIGNED — see `ServerDeduction`. The column is read downward.
            value: (
              <ServerDeduction
                value={document.totals.discount_total}
                currency={document.currency}
              />
            ),
          },
          ...document.taxes.map((tax, index) => ({
            key: `tax-${String(index)}`,
            label: `${tax.tax_name} @ ${tax.rate}%`,
            detail: `on ${formatMoney(tax.base_amount, document.currency)}`,
            value: <ServerAmount value={tax.tax_amount} currency={document.currency} />,
          })),
          {
            key: 'tax-total',
            // `tax_total` — the figure `grand_total` was built from, ALWAYS
            // rendered (W4): with no named row, the amount must not vanish
            // above a grand total containing it.
            label: 'Tax',
            ...(document.taxes.length === 0 ? { detail: 'No tax breakdown was resolved' } : {}),
            value: <ServerAmount value={document.totals.tax_total} currency={document.currency} />,
          },
        ];

  const defaultTaxGroup = taxGroups.data?.find((group) => group.is_default);

  return (
    <div className="flex flex-col gap-4 p-6">
      <PageHead
        title={isOrder ? 'New sales order' : 'New quote'}
        sub={
          // `hasRequest` as well: the preview keeps its previous answer while the next one is
          // asked (`keepPreviousData`), so after a currency change clears the lines the old
          // answer — and its currency — would otherwise stay in the title (found at review).
          document === undefined || !hasRequest
            ? `Nothing is saved yet. Priced by the server, in ${countryCode}${
                currency === null ? '' : ` · ${currency}`
              }.`
            : `Nothing is saved yet. Priced for ${document.doc_date} in ${document.currency} · ` +
              /* From the RESPONSE, not the select: picked or defaulted, the
               * server reports which group actually ran (U3). */
              `Tax group: ${document.tax_group_name}.`
        }
        actions={
          <>
            <Link
              to={`/countries/${countryCode}/${isOrder ? 'sales-orders' : 'quotes'}`}
              className="inline-flex h-9 items-center rounded-sm border border-rim px-3 text-body font-medium text-fg hover:bg-subtle"
            >
              Cancel
            </Link>
            {/*
              * ONE BUTTON, TWO ACTS, AND IT DISAPPEARS ONCE THE ORDER EXISTS. While
              * `savedDraft` is held the primary control is the RETRY inside the outcome
              * panel below — leaving this one live would offer "Place order" beside a
              * quote that has already been placed, and pressing it would create a second.
              */}
            {isOrder && (savedDraft !== null || placedOrder !== null) ? null : (
              <Button
                onClick={isOrder ? placeOrder : () => save()}
                disabled={blockedReason !== null || busy}
              >
                {isOrder
                  ? busy
                    ? 'Placing…'
                    : 'Place order'
                  : create.isPending
                    ? 'Saving…'
                    : recovery !== null
                      ? 'Issue recovery document'
                      : 'Save draft'}
              </Button>
            )}
          </>
        }
      />

      {create.isError ? (
        <Alert tone="danger" title={isOrder ? 'The order was not placed' : 'The quote was not saved'}>
          {quoteErrorText(create.error)}
          {isOrder ? ' Nothing was written — no quote and no order.' : null}
        </Alert>
      ) : null}

      {/*
       * ═══════════════════════════════════════════════════════════════════════
       * THE HALF-DONE STATE, RENDERED RATHER THAN SWALLOWED
       * ═══════════════════════════════════════════════════════════════════════
       * The create succeeded and the confirm did not. A DRAFT QUOTE EXISTS, holding a
       * document number. The alternative treatments were both considered and are both
       * dishonest: showing only the error reads as "nothing happened" (and the operator
       * presses the button again, burning a second number), and silently deleting the
       * quote would need a DELETE route that does not exist and must not.
       *
       * The retry confirms THAT quote. It is the only control offered, so there is no
       * path from here to a second draft.
       */}
      {isOrder && savedDraft !== null ? (
        <Alert tone="warning" title={`${savedDraft.doc_no} was saved, but the order was not created`}>
          <p>{confirmError === null ? 'The confirm did not complete.' : quoteErrorText(confirmError)}</p>
          <p className="mt-2">
            The quote exists as a draft and holds its number. Fix what the message names, then
            confirm this same quote — pressing Place order again would raise a second one.
          </p>
          <div className="mt-3 flex flex-wrap items-center gap-3">
            <Button onClick={() => confirmInto(savedDraft.id)} disabled={confirm.isPending}>
              {confirm.isPending ? 'Confirming…' : `Confirm ${savedDraft.doc_no}`}
            </Button>
            {canSeeQuotes ? (
              <Link
                to={`/countries/${countryCode}/quotes/${savedDraft.id}`}
                className="text-body13 text-fg underline underline-offset-2 hover:decoration-2"
              >
                Open the draft quote
              </Link>
            ) : null}
          </div>
        </Alert>
      ) : null}

      {/*
        DE 74/19 33 c) (AGT U2): the record page's clock question, on the same draft — or, for a
        recovery document (AGT U7, frozen as it is saved), on the same request sent again.
      */}
      <ConfirmModal
        open={clockRetry !== null && (savedDraft !== null || recovery !== null)}
        title="Confirm the date and time"
        body={clockRetry === null ? '' : clockBehindSentence(clockRetry)}
        confirmLabel="Confirm and issue"
        busy={confirm.isPending || create.isPending}
        onCancel={() => setClockRetry(null)}
        onConfirm={() => {
          if (savedDraft !== null) confirmInto(savedDraft.id, true);
          else if (recovery !== null) save(true);
        }}
      />

      {/*
       * ORDER PLACED, AND THIS ACCOUNT CANNOT OPEN IT. `so.view` is separately grantable
       * from `quotes.confirm` (Appendix A), and a seat holding the second without the
       * first can create an order it has no screen for — the shape filed as
       * OQ-TALLY-SALES-SO-VIEW. Navigating there anyway would answer with a permission
       * refusal and read as a failure; this states the fact and the number instead.
       */}
      {isOrder && placedOrder !== null ? (
        <Alert
          tone="info"
          title={
            placedOrder.sales_order === null
              ? `${placedOrder.quote.doc_no} was confirmed`
              : `${placedOrder.sales_order.doc_no} was created`
          }
        >
          <p>
            {placedOrder.sales_order === null
              ? 'The quote is confirmed and no sales order was created for it.'
              : 'The order is recorded, awaiting Tally sync — the push is Phase 3b and nothing ' +
                'has been sent to Tally. Nobody is notified: no notification rule names a ' +
                'confirmed quote or a new order.'}
          </p>
          <p className="mt-2">
            This account does not hold the Sales Orders permission in {countryCode}, so the
            order cannot be opened from here.
          </p>
        </Alert>
      ) : null}

      {!canSeeAmounts ? (
        <Alert tone="info" title="Amounts are hidden for this account">
          The server is still pricing this {isOrder ? 'order' : 'quote'} — it simply will not
          show you the figures, because this account does not hold the Amounts permission in{' '}
          {countryCode}.{' '}
          {isOrder
            ? 'Placing the order works, and the totals are stored as the server computed them ' +
              '— you will be committing to money you cannot see on this screen.'
            : 'Saving works; the totals are stored as the server computed them.'}
        </Alert>
      ) : null}

      <div className="flex flex-col gap-4 xl:flex-row xl:items-start">
        {/* ═══ the main column — the numbered cards ═════════════════════════ */}
        <div className="flex min-w-0 flex-1 flex-col gap-4">
          {/*
            * THE CURRENCY — STEP 1 WHERE THERE IS A CHOICE (owner, 2026-09-22). Congo quotes in
            * CDF or USD from two fixed lists and nothing converts between them, so the list is
            * chosen before anything is priced. Nothing is preselected. A one-currency country
            * never sees this card, and its steps keep their numbers.
            */}
          {currencies.isError ? (
            <Alert tone="danger" title="The currencies could not be loaded">
              {quoteErrorText(currencies.error)} Nothing can be priced until they are.{' '}
              <button
                type="button"
                onClick={() => void currencies.refetch()}
                className="underline underline-offset-2 hover:decoration-2"
              >
                Try again
              </button>
            </Alert>
          ) : null}
          {choiceRequired ? (
            <StepCard
              n={1}
              state={currency === null ? 'now' : 'done'}
              title="Currency"
              badge={currency === null ? <InfoPill>Current</InfoPill> : <OkPill>Chosen</OkPill>}
            >
              <fieldset className="flex flex-col gap-2">
                <legend className="text-label text-fg-secondary">
                  Which currency is this quote in? Each currency has its own fixed price list,
                  and prices are never converted between them.
                </legend>
                <div className="flex flex-wrap gap-3" role="radiogroup" aria-label="Quote currency">
                  {(currencies.data?.currencies ?? []).map((code) => (
                    <label
                      key={code}
                      className={cn(
                        'flex cursor-pointer items-center gap-2 rounded-sm border px-3 py-2 text-body text-fg',
                        (pendingCurrency ?? currency) === code
                          ? 'border-fg bg-subtle'
                          : 'border-line hover:bg-subtle',
                      )}
                    >
                      <input
                        type="radio"
                        name="quote-currency"
                        id={`quote-currency-${code}`}
                        value={code}
                        checked={(pendingCurrency ?? currency) === code}
                        onChange={() => chooseCurrency(code)}
                      />
                      {currencyLabel(code)}
                    </label>
                  ))}
                </div>
              </fieldset>
              {/*
                * Rendered whenever a change is pending — found at review: gated on a VALID
                * current currency, a hand-off carrying a currency the country no longer quotes
                * in left the radio ticking with nothing to confirm.
                */}
              {pendingCurrency !== null ? (
                <Alert tone="warning" title={`Change this quote to ${pendingCurrency}?`}>
                  <p>
                    Changing to {pendingCurrency} removes the lines you added — rates typed in{' '}
                    {currency ?? chosenCurrency ?? 'the earlier currency'} cannot be carried into{' '}
                    {pendingCurrency}. The customer stays.
                  </p>
                  <div className="mt-3 flex flex-wrap gap-3">
                    <Button onClick={confirmCurrencyChange}>
                      Change to {pendingCurrency} and clear the lines
                    </Button>
                    <Button variant="ghost" onClick={() => setPendingCurrency(null)}>
                      {currency === null ? 'Cancel' : `Keep ${currency}`}
                    </Button>
                  </div>
                </Alert>
              ) : null}
            </StepCard>
          ) : null}

          <StepCard
            n={choiceRequired ? 2 : 1}
            state={
              choiceRequired && currency === null ? 'next' : party === null ? 'now' : 'done'
            }
            title="Customer"
            badge={
              choiceRequired && currency === null ? (
                <NeutralPill>After the currency</NeutralPill>
              ) : party === null ? (
                <InfoPill>Current</InfoPill>
              ) : (
                <OkPill>Chosen</OkPill>
              )
            }
          >
            {choiceRequired && currency === null ? (
              <p className="text-label text-fg-secondary">Choose the currency first.</p>
            ) : (
              <PartyPicker
                countryCode={countryCode}
                party={party}
                onSelect={setParty}
                onClear={() => setParty(null)}
                onCreateProspect={handOffToPartyCreate}
              />
            )}
          </StepCard>

          {/*
           * THE COST OF THE SHAPE, ON THE SCREEN THAT INCURS IT. A direct order consumes
           * a QT number as well as an SO number, because the quote is the pricing
           * worksheet and its number is the audit trail of it. An operator who later
           * finds a confirmed quote they never sent should have read this first.
           */}
          {isOrder ? (
            <p className="text-label text-fg">
              Placing the order records a quote as the priced worksheet behind it and confirms
              it immediately, so a quote number is consumed as well as an order number. The
              quote will show as confirmed and will never have been sent.
            </p>
          ) : null}

          <StepCard
            n={choiceRequired ? 3 : 2}
            state={currency === null || party === null ? 'next' : 'now'}
            title="Lines"
            badge={
              choiceRequired && currency === null ? (
                <NeutralPill>After the currency</NeutralPill>
              ) : party === null ? (
                <NeutralPill>After the customer</NeutralPill>
              ) : (
                <InfoPill>Current</InfoPill>
              )
            }
            headerAction={
              <span className="tnum text-label text-fg-secondary">
                {lines.length} of {MAX_QUOTE_LINES}
              </span>
            }
          >
            {currency === null ? (
              <p className="text-label text-fg-secondary">
                {choiceRequired
                  ? 'Choose the currency first — the products offered are the ones priced in it.'
                  : `Loading the ${countryCode} catalogue…`}
              </p>
            ) : catalogue.isPending ? (
              <p className="text-label text-fg-secondary">Loading the {countryCode} catalogue…</p>
            ) : catalogue.isError ? (
              <Alert tone="danger" title="The catalogue could not be loaded">
                {quoteErrorText(catalogue.error)} Without it there is nothing to quote.
              </Alert>
            ) : (catalogue.data ?? []).length === 0 ? (
              <Alert
                tone="warning"
                title={
                  choiceRequired
                    ? `Nothing is priced in ${currency} in this country`
                    : 'Nothing is priced for sale in this country'
                }
              >
                A product needs a row in this country&rsquo;s{' '}
                {choiceRequired ? `${currency} ` : ''}price list before it can be quoted. An
                administrator sets that on the Products screen.
              </Alert>
            ) : choiceRequired ? (
              <p className="text-label text-fg-secondary">
                Only products with a {currency} price in {countryCode} are listed; an
                administrator sets prices on the Products screen.
              </p>
            ) : null}

            {/*
              * §B2 — A REQUIRED LINE, RENDERED AS ONE.
              *
              * ⚠ THIS WAS A DEFECT, NOT A STYLE CHOICE, AND IT IS FIXED AS ONE.
              * The requirement previously rendered in the builder's generic
              * "why Save is off" slot: `text-label text-fg-secondary`, the same
              * weight as "Pick a customer and at least one product." A LICENCE
              * CONDITION read as a hint. The owner ruled the low salience a
              * defect — "a mandatory legal condition should read as required
              * before save, not as a 422 after" — and that was the brief's
              * intent all along.
              *
              * `tone="danger"`, not `warning`: warning is for something that
              * might be wrong. This is a term of the licence, and the document
              * cannot be saved without it.
              *
              * It sits INSIDE the Lines card, above the editors, because that
              * is where the missing line would go. A banner at the top of the
              * page states a fact about the document; this states a fact about
              * the thing directly beneath it.
              */}
            {patchReason === null ? null : (
              <Alert tone="danger" title="A government patch line is required">
                <p>{patchReason}</p>
                <p className="mt-1 font-medium">
                  This quote cannot be saved until it is added.
                </p>
              </Alert>
            )}

            {lines.map((line, index) => (
              <LineEditor
                key={line.key}
                index={index}
                line={line}
                catalogue={catalogue.data ?? []}
                entry={catalogueById.get(line.product_id) ?? null}
                pricedTotal={pricedTotalFor(line.key)}
                currency={document?.currency ?? null}
                quoteCurrency={choiceRequired ? currency : null}
                behind={previewBehind}
                onPatch={(patch) => patchLine(line.key, patch)}
                onChooseProduct={(productId) => chooseProduct(line.key, productId)}
                onRemove={() => removeLine(line.key)}
                removable={lines.length > 1}
              />
            ))}

            <div>
              <Button
                variant="ghost"
                onClick={addLine}
                disabled={lines.length >= MAX_QUOTE_LINES}
              >
                <Plus aria-hidden="true" className="size-4" />
                Add line
              </Button>
            </div>

            {/* ── the priced foot of the card — figures, or their honest absence ── */}
            {!hasRequest ? (
              <div className="rounded-sm border border-dashed border-line-strong p-4">
                <p className="text-body text-fg-secondary">
                  Pick a customer and a product. The server prices the document and the figures
                  appear here — this screen never calculates a total of its own.
                </p>
              </div>
            ) : preview.isError ? (
              <Alert tone="danger" title="The server could not price this document">
                {/*
                 * The API's OWN sentence. `quote-pricing.service.ts` writes refusals that
                 * name the fix ("Set it with PUT /countries/:cc/products before quoting
                 * them", or the exact field of a rate it will not round); replacing them
                 * with a house string would delete the only useful part.
                 */}
                {quoteErrorText(preview.error)}
              </Alert>
            ) : document === undefined ? (
              <div
                aria-busy="true"
                aria-live="polite"
                className="rounded-sm border border-line p-4"
              >
                <p className="text-body text-fg-secondary">Asking the server for a price…</p>
              </div>
            ) : (
              <>
                {document.fx_stale ? (
                  <Alert tone="warning" title="The FX rate used is from an earlier period">
                    {/* D2 step 2. The customer document does not print the rate (decision
                      * U4-4 — it is HQ's intra-group reporting figure), but the person
                      * quoting should know the INR-normalised reporting value behind this
                      * quote is based on an older rate. */}
                    The {document.currency} figures are correct. The group&rsquo;s INR reporting
                    value for this quote uses the rate from{' '}
                    {document.fx_period ?? 'an earlier period'}.
                  </Alert>
                ) : null}

                {/*
                  * S8's cheap, NON-BLOCKING hint (owner ruling OD-2) — and since C2 it
                  * names the RIGHT record. The C2 ruling, VERBATIM (2026-08-26,
                  * docs/OQ-GST-NULL-HOME-STATE.md §6): "A hint that tells a coordinator
                  * to fix the party when the seller's own state is missing sends them to
                  * the wrong screen, and they will not find the problem there.
                  * Distinguish PARTY_STATE_UNSET from SELLER_STATE_UNSET."
                  *
                  * The BOOLEAN stays the render gate (every pre-C2 behaviour preserved);
                  * the REASON only picks the copy. Never a blocker — a quote never blocks
                  * on a tax question; Tally's invoice is the tax truth. Three copies:
                  *   · SELLER_STATE_UNSET — the party is NOT the problem; the fix is the
                  *     country's own state, reached the way the nav names it ("Country
                  *     settings"), with the ordering rule an operator needs (state first,
                  *     then the tax group — the doors in countries.service.ts refuse the
                  *     other order).
                  *   · PARTY_AND_SELLER_STATE_UNSET — BOTH records named, neither hidden
                  *     behind the other (the no-precedence choice, argued on the server
                  *     type): fixing only one still leaves the branch undecided.
                  *   · PARTY_STATE_UNSET (and the guard's fallback) — the pre-C2 copy,
                  *     unchanged: the unregistered/B2C party is the COMMON case (S8).
                  */}
                {document.tax_state_unresolved ? (
                  document.tax_state_unresolved_reason === 'SELLER_STATE_UNSET' ? (
                    <Alert tone="info" title="This country’s own state is unset — showing IGST">
                      The customer&rsquo;s record is fine — their state resolved. What is
                      missing is this country&rsquo;s own state, so every quote falls to the
                      inter-state IGST line, even for a customer in the seller&rsquo;s own
                      state. Fixing the party will not clear this: set the country&rsquo;s
                      own state under Country settings (state first, then the tax group) and
                      re-price. The total matches within a paisa either way, and
                      Tally&rsquo;s invoice remains the tax truth.
                    </Alert>
                  ) : document.tax_state_unresolved_reason === 'PARTY_AND_SELLER_STATE_UNSET' ? (
                    <Alert tone="info" title="State unresolved — showing IGST">
                      Two records are missing a state: this party has no GSTIN or state on
                      record, and this country&rsquo;s own state is unset under Country
                      settings. Fixing only one still leaves the branch undecided — record
                      the customer&rsquo;s GSTIN or state on the party, set the
                      country&rsquo;s own state (state first, then the tax group), and
                      re-price. Tally&rsquo;s invoice remains the tax truth.
                    </Alert>
                  ) : (
                    <Alert tone="info" title="State unresolved — showing IGST">
                      This party has no GSTIN or state on record, so the quote shows the
                      inter-state IGST line. If the customer is in your own state, record their
                      GSTIN or state on the party and re-price for the CGST/SGST pair. The total
                      matches within a paisa either way, and Tally&rsquo;s invoice remains the
                      tax truth.
                    </Alert>
                  )
                ) : null}

                {/* The mockup's `tot-wrap`: the stack right-aligned under the
                  * lines, inside the same card — the record's own geometry. The
                  * wrapper carries W1's whole stale treatment: caption,
                  * aria-busy, and a 150ms opacity settle (motion-safe only). */}
                <div className="flex justify-end border-t border-line pt-3">
                  <div
                    aria-busy={previewBehind}
                    className={cn(
                      'flex w-full max-w-[360px] flex-col',
                      'motion-safe:transition-opacity motion-safe:duration-settle',
                      previewBehind && 'opacity-60',
                    )}
                  >
                    <div className="flex items-center justify-between gap-3 pb-1">
                      <h3 className="text-th uppercase text-fg-secondary">Totals</h3>
                      <span className="text-label text-fg-secondary">
                        {previewBehind ? 'Re-pricing…' : 'Priced by the server'}
                      </span>
                    </div>
                    <TotalsBlock
                      rows={totalsRows}
                      grand={{
                        label: 'Grand total',
                        value: (
                          <ServerAmount
                            value={document.totals.grand_total}
                            currency={document.currency}
                          />
                        ),
                      }}
                    />
                    {/*
                     * D6's own sentence: the document is indicative and the invoice
                     * governs. Printed on the PDF (`quote-document.template.ts`); the
                     * person quoting should read the same disclaimer the customer will.
                     */}
                    <p className="pt-2 text-label text-fg-secondary">
                      Taxes as applicable; invoice values per the selling entity&rsquo;s
                      records govern.
                    </p>
                  </div>
                </div>
              </>
            )}
          </StepCard>

          <ReadyBar statement={readyStatement} />
        </div>

        {/* ═══ the aside — the document's properties ════════════════════════ */}
        <aside className="flex w-full shrink-0 flex-col gap-4 xl:w-[360px]">
          <RecordCard title="Document">
            {/* The quote's currency (2026-09-22) — fixed once the draft is saved. */}
            <div className="flex flex-col gap-1">
              <span className="text-th uppercase text-fg-secondary">Currency</span>
              <p className="text-body13 text-fg" data-testid="quote-currency">
                {currency === null ? 'Not chosen yet' : currencyLabel(currency)}
              </p>
              {currency !== null && choiceRequired ? (
                <p className="text-label text-fg-secondary">
                  Fixed once the quote is saved. The same deal in the other currency is a new
                  quote.
                </p>
              ) : null}
            </div>

            {/*
              * ═══════════════════════════════════════════════════════════════
              * WHAT KIND OF DOCUMENT THIS IS — SHOWN, NOT ASKED (rulings R1/Q3).
              * ═══════════════════════════════════════════════════════════════
              * ⚠ THE RADIO IS GONE. A `<fieldset>` with two options stood here, one per
              * type, each with its confirm-time consequence as its description, plus an
              * order-mode branch that stated `CRF customisation` and rendered no control.
              * All of it is retired: `quotes.type` is a fact about the lines, so asking
              * the operator to declare it was asking them to state something the basket
              * below already answers — and letting them answer differently was the
              * mis-filing the derivation exists to kill.
              *
              * ⚠ ONE BRANCH, NOT TWO. The order-mode arm went with the radio and did not
              * come back. It used to be able to promise `CRF customisation` because order
              * mode FIXED the type; nothing fixes it now, so a direct order whose basket
              * carries a Tally licence is a Tally document and raises no sales order —
              * which the consequence line below says, in the same words the quote flow
              * uses. Saying it once means the two flows cannot come to disagree.
              */}
            <div className="flex flex-col gap-1">
              <span className="text-th uppercase text-fg-secondary">Quote type</span>
              <p className="text-body13 text-fg" data-testid="quote-type-derived">
                {QUOTE_TYPE_LABELS[derivedType]}
              </p>
              <p className="text-label text-fg-secondary">
                {derivedType === 'crf'
                  ? 'Set by the lines: no Tally licence on this document. Confirming raises a sales order.'
                  : derivedType === 'tally_sales'
                    ? 'Set by the lines: every line is a Tally licence. Confirming stops here — the licence is raised in Tally by hand, and no sales order is created.'
                    : 'Set by the lines: a Tally licence alongside other work. Confirming stops here — no sales order is created, because the licence is raised in Tally by hand and a part-order for the rest is not something this build writes.'}
              </p>
              <p className="text-label text-fg-secondary">
                {isOrder
                  ? 'This is not a choice — the products decide it, and it can still change until the order is placed.'
                  : 'This is not a choice — the products decide it. It can still change while the quote is a draft, and it is fixed when the quote is sent.'}
              </p>
            </div>

            {/*
              * WHICH TAX GROUP (migration 30, OD-1 — design U3). A document property
              * like the quote type above. "Country default" is the RESTING state and a
              * real choice: the request then omits `tax_group_id` and the server
              * resolves the default — the exact pre-picker behaviour, stated as an
              * option instead of hidden as one.
              *
              * RENDERED ONLY WHEN THE LIST LOADED WITH SOMETHING TO PICK. While it is
              * loading — or if it fails — the builder must keep quoting: the server
              * defaults the group either way, so the absent control never blocks and
              * never lies. Retired groups are not in this list (M6).
              */}
            {(taxGroups.data ?? []).length === 0 ? null : (
              <div className="flex flex-col gap-1.5 border-t border-line pt-3">
                <SelectField
                  label="Tax group"
                  id="quote-tax-group"
                  options={[
                    {
                      value: '',
                      label: `Country default${
                        defaultTaxGroup === undefined
                          ? ''
                          : ` — ${(defaultTaxGroup as TaxGroupDto).name}`
                      }`,
                    },
                    ...(taxGroups.data ?? []).map((group) => ({
                      value: group.id,
                      label: group.name,
                    })),
                  ]}
                  value={taxGroupId}
                  onChange={setTaxGroupId}
                />
                <p className="text-label text-fg-secondary">
                  Names a tax configuration for this document. The server prices it either
                  way — Tally&rsquo;s invoice remains the tax truth.
                </p>
              </div>
            )}

            <div className="flex flex-col gap-1.5 border-t border-line pt-3">
              <TextField
                label="Valid till"
                type="date"
                value={validTill}
                onChange={(event) => setValidTill(event.target.value)}
              />
              <p className="text-label text-fg-secondary">
                {/* Honest about scope: `valid_till` changes NO number. The API stores it
                  * and the PDF prints it; nothing expires a quote on its own in this
                  * phase (`expired` is an enum member no route writes). */}
                Printed on the document. Nothing expires a quote automatically in this phase.
              </p>
            </div>
          </RecordCard>

          {canRecover ? (
            <RecordCard title="Recovery document">
              {/* AGT U7 (DE 74/19 8, 9): kind, the original's identifiers apart, its date — then the recover route. */}
              <SelectField
                label="Recovery"
                id="quote-recovery-kind"
                options={[
                  { value: '', label: 'No — an ordinary quote' },
                  { value: 'M', label: 'Manual — written by hand during an outage' },
                  { value: 'D', label: 'Duplicate — issued, then lost in a restore' },
                ]}
                value={recoveryKind}
                onChange={(value) => setRecoveryKind(value as '' | 'M' | 'D')}
              />
              {recoveryKind === '' ? null : (
                <>
                  {recoveryKind === 'D' ? (
                    <TextField
                      label="Original type code"
                      placeholder="OR"
                      maxLength={2}
                      value={recoveryTypeCode}
                      onChange={(event) => setRecoveryTypeCode(event.target.value)}
                    />
                  ) : null}
                  <TextField
                    label="Original série"
                    placeholder={recoveryKind === 'D' ? 'AOQT2026' : 'ab'}
                    maxLength={40}
                    value={recoverySerie}
                    onChange={(event) => setRecoverySerie(event.target.value)}
                  />
                  <TextField
                    label="Original number"
                    placeholder={recoveryKind === 'D' ? '7' : '0001'}
                    maxLength={19}
                    inputMode="numeric"
                    value={recoveryNumber}
                    onChange={(event) => setRecoveryNumber(event.target.value)}
                  />
                  <TextField
                    label="Original date"
                    type="date"
                    max={toDateInputValue(new Date().toISOString())}
                    value={recoveryDate}
                    onChange={(event) => setRecoveryDate(event.target.value)}
                  />
                  <p className="text-label text-fg-secondary">
                    Numbered in the {recoveryKind === 'M' ? 'manual' : 'duplicate'} recovery série,
                    sent and signed at once, dated on the original&rsquo;s date. It cannot be
                    edited afterwards.
                  </p>
                </>
              )}
            </RecordCard>
          ) : null}

          {/* Not for a recovery document: it is numbered, sent and signed at once (its card above says so). */}
          {recovery === null ? (
            <RecordCard title={isOrder ? 'What Place order does' : 'What Save draft does'}>
              <p className="text-body13 text-fg-secondary">
                {isOrder
                  ? 'Writes the quote as the priced worksheet and confirms it in one step — two ' +
                    'requests, and the screen names the half-done state if the second is refused. ' +
                    'The order lands awaiting Tally sync.'
                  : countryProfile(countryCode).saftAo
                    ? // AGT séries (design §3 U1): once the série is open a draft is numbered when
                      // it is SENT, not saved. The page cannot tell whether it is open, so it says both.
                      `Issues the next number in the ${countryCode}-QT series and writes the ` +
                      'document with the lines as priced above — until the ' +
                      `${countryCode} AGT série is opened; once it is open, a draft is numbered when ` +
                      'it is sent, not when it is saved. A draft can still be re-priced; a sent ' +
                      'quote cannot.'
                    : `Issues the next number in the ${countryCode}-QT series and writes the document ` +
                      'with the lines as priced above. A draft can still be re-priced; a sent quote ' +
                      'cannot.'}
              </p>
              <p className="text-body13 text-fg-secondary">
                There is no draft storage on this screen. Cancel discards the form and no number
                is issued.
              </p>
            </RecordCard>
          ) : null}
        </aside>
      </div>
    </div>
  );
}

/* ────────────────────────────────────────────────────────────── line editor ── */

function LineEditor({
  index,
  line,
  catalogue,
  entry,
  pricedTotal,
  currency,
  quoteCurrency,
  behind,
  onPatch,
  onChooseProduct,
  onRemove,
  removable,
}: {
  index: number;
  line: DraftLine;
  catalogue: readonly CatalogueEntry[];
  entry: CatalogueEntry | null;
  /** The SERVER's `line_total` for this line, or null while it does not price.
   * Positional mapping — see the page's WT6 header block. */
  pricedTotal: string | null;
  currency: string | null;
  /** The quote's chosen currency, for the rate's label — known before any preview. */
  quoteCurrency: string | null;
  /** W1: the figure below belongs to an earlier basket than the form. */
  behind: boolean;
  onPatch: (patch: Partial<DraftLine>) => void;
  /** Separate from `onPatch` because choosing a product also prefills the rate. */
  onChooseProduct: (productId: string) => void;
  onRemove: () => void;
  removable: boolean;
}): React.JSX.Element {
  /*
   * U9 — THE TWO-STEP PICKER (tax-groups design §4). Step 1 narrows to a shelf, step 2
   * picks the item. All of it is derived from the catalogue payload this page already
   * fetched — a group with zero pickable items has nothing to pick, so distinct groups
   * OF THE CATALOGUE is the correct set, not a shortcut around `GET /product-groups`.
   *
   *  · "All items" is first AND the resting state, so the one-picker flow stays a
   *    click away.
   *  · Shelves arrive in catalogue order, which `compareCatalogue` already sorts by
   *    the country's own arrangement (sort_order, then name) — deriving order here
   *    again would be a second opinion.
   *  · "Ungrouped" renders LAST, only when unfiled items exist: NULL is the absence of
   *    a group, not a sentinel (migration 23), so the bucket is a view convenience and
   *    its select VALUE below is UI state, never data.
   *  · Purely presentational: filtering which items are SHOWN changes no request and
   *    no price — `product-groups.e2e-spec.ts` stays green with zero edits, and the
   *    priced-line test proves the narrowed pick sends the same product_id.
   *
   * ⚠ WT6: the ITEM picker is the vendored Combobox now, whose flat filtered list
   * replaces the native select's `<optgroup>` headings — the SHELF select is the
   * grouping surface, and typing in the Combobox searches name and code across
   * whatever the shelf shows. A country that has not filed its catalogue still sees
   * no shelf select at all (U3.4's load-bearing half, unchanged).
   */
  const [shelf, setShelf] = useState('');
  const anyFiled = catalogue.some((product) => product.product_group_name !== null);
  const shelves: { id: string; name: string }[] = [];
  for (const product of catalogue) {
    if (product.product_group_id === null) continue;
    const id = String(product.product_group_id);
    if (!shelves.some((candidate) => candidate.id === id)) {
      shelves.push({ id, name: product.product_group_name ?? 'Ungrouped' });
    }
  }
  const hasUngrouped = catalogue.some((product) => product.product_group_id === null);
  // Group ids are numeric strings, so the literal cannot collide with one.
  const UNGROUPED_SHELF = 'ungrouped';
  const shelfOptions = [
    { value: '', label: 'All items' },
    ...shelves.map((candidate) => ({ value: candidate.id, label: candidate.name })),
    ...(hasUngrouped ? [{ value: UNGROUPED_SHELF, label: 'Ungrouped' }] : []),
  ];
  const visibleCatalogue =
    shelf === ''
      ? catalogue
      : shelf === UNGROUPED_SHELF
        ? catalogue.filter((product) => product.product_group_id === null)
        : catalogue.filter((product) => String(product.product_group_id) === shelf);
  /*
   * THE PICKED ITEM SURVIVES A FILTER THAT HIDES IT. The Combobox's trigger shows
   * the option whose value matches — an option list missing the picked id would
   * show the placeholder while the form still held the old id, a control lying
   * about the document. So a selection from outside the current shelf is pinned
   * in as an extra option rather than dropped.
   */
  const pinned =
    entry !== null &&
    line.product_id !== '' &&
    !visibleCatalogue.some((product) => product.product_id === line.product_id)
      ? [entry]
      : [];
  /**
   * The catalogue AS-IS, holes included (WT6 header): the price slot renders the
   * list price where one exists and the NO_VALUE dash where the row carries the
   * seeded placeholder `0.00` (or a masked value) — never an invented figure.
   */
  const optionOf = (product: CatalogueEntry): {
    value: string;
    label: string;
    code: string;
    detail: string | null;
  } => {
    const listPrice = listPriceOf(product);
    return {
      value: product.product_id,
      label: product.product_name,
      code: product.product_code,
      // A blank list price is a fact worth showing, not a hole: the price is typed per quote.
      detail: product.priced_per_customer
        ? 'Priced per customer'
        : listPrice === null
          ? null
          : formatMoney(listPrice, product.currency),
    };
  };
  const options = [...pinned.map(optionOf), ...visibleCatalogue.map(optionOf)];

  return (
    <div className={cn('flex flex-col gap-3 rounded-sm border border-line p-3')}>
      <div className="flex items-start justify-between gap-3">
        <span className="tnum pt-2 text-label text-fg-secondary">{index + 1}</span>
        <div className={cn('min-w-0 flex-1', anyFiled && 'flex flex-col gap-2')}>
          {anyFiled ? (
            <SelectField
              label="Product group"
              id={`product-group-${line.key}`}
              options={shelfOptions}
              value={shelf}
              onChange={setShelf}
            />
          ) : null}
          <div className="flex flex-col gap-1.5">
            <label htmlFor={`product-${line.key}`} className="text-label font-medium text-fg">
              Product
            </label>
            <Combobox
              id={`product-${line.key}`}
              options={options}
              value={line.product_id === '' ? null : line.product_id}
              onSelect={onChooseProduct}
              listLabel="Product"
              placeholder="Choose a product…"
              searchPlaceholder="Search the catalogue"
              emptyText="No product matches."
            />
          </div>
        </div>
        {removable ? (
          <button
            type="button"
            aria-label={`Remove line ${String(index + 1)}`}
            onClick={onRemove}
            className="mt-6 flex size-9 shrink-0 items-center justify-center rounded-sm border border-line text-fg-secondary hover:bg-subtle hover:text-fg"
          >
            <Trash2 aria-hidden="true" className="size-4" />
          </button>
        ) : null}
      </div>

      <div className="flex flex-wrap items-start gap-3">
        <div className="w-28">
          <TextField
            label="Qty"
            inputMode="decimal"
            value={line.qty}
            error={qtyError(line.qty)}
            onChange={(event) => onPatch({ qty: event.target.value })}
          />
        </div>
        <div className="w-44">
          <TextField
            // The quote's currency on the label where the country has a choice (2026-09-22): a
            // Congo salesperson must never have to guess whether the number is francs or
            // dollars. A one-currency country's label is unchanged.
            label={quoteCurrency === null ? 'Rate' : `Rate (${quoteCurrency})`}
            inputMode="decimal"
            // The list price, shown as the placeholder so an emptied field still
            // says what the server will fall back to. A placeholder-priced row
            // has no figure to show, so the words say where the fallback lives —
            // and a row priced per customer (R3) has no fallback at all.
            placeholder={
              pricedPerCustomer(entry)
                ? "This customer's price"
                : (listPriceOf(entry) ?? 'Price list')
            }
            value={line.unit_rate}
            error={
              // Only once a product is chosen: an untouched blank line is not an error.
              line.product_id === ''
                ? rateError(line.unit_rate)
                : rateError(line.unit_rate, pricedPerCustomer(entry))
            }
            onChange={(event) => onPatch({ unit_rate: event.target.value })}
          />
        </div>
        <div className="w-28">
          <TextField
            label="Discount %"
            inputMode="decimal"
            placeholder="0"
            value={line.discount_pct}
            error={pctError(line.discount_pct)}
            onChange={(event) => onPatch({ discount_pct: event.target.value })}
          />
        </div>
        <div className="min-w-[180px] flex-1">
          <TextField
            label="Description override"
            maxLength={300}
            placeholder={entry?.product_name ?? 'Defaults to the product name'}
            value={line.description}
            onChange={(event) => onPatch({ description: event.target.value })}
          />
        </div>
        {/*
         * THE SERVER'S FIGURE, IN THE ROW THAT PRODUCED IT (WT6 — forms.html
         * screen 2's "Line total" column, on the editor the real page has).
         * NO_VALUE while the line does not price; the W1 stale treatment
         * (opacity + the stack's caption) while the basket is ahead of it.
         */}
        <div className="ml-auto flex w-32 flex-col items-end gap-1.5 pt-0.5">
          <span className="text-label font-medium text-fg">Line total</span>
          <span
            className={cn(
              'tnum text-body font-medium text-fg',
              'motion-safe:transition-opacity motion-safe:duration-settle',
              behind && 'opacity-60',
            )}
          >
            {pricedTotal === null ? (
              NO_VALUE
            ) : (
              <ServerAmount value={pricedTotal} currency={currency} />
            )}
          </span>
        </div>
      </div>

      {/*
       * §RENEWAL — THE TERM AND THE EXPIRY (migration 18).
       *
       * Shown only for a product the CATALOGUE marks term-bearing, which is the same
       * shape as `verify_before_prod` above: a per-product flag arriving on the picker
       * route drives per-line UI, so nothing here hardcodes a product code.
       *
       * BOTH FIELDS MAY BE LEFT BLANK. Blank asks the server to prefill — one year, and
       * the day before the anniversary of the sale date. The prefill is echoed back on
       * the priced line, so the operator sees what will be stored before they save.
       *
       * ⚠ AND THE DATE IS FREELY EDITABLE, WHICH IS THE WHOLE FEATURE. Renewal offers
       * exist where paying for twelve months gives thirteen; a rigid derived date would
       * be wrong in exactly the cases that are worth money. Nothing here — and nothing on
       * the server — checks a typed date against the derived one.
       */}
      {entry !== null && entry.term_bearing ? (
        <div className="flex flex-wrap items-start gap-3">
          <div className="w-32">
            <label className="text-label text-fg-secondary" htmlFor={`${line.key}-term`}>
              Term
            </label>
            <select
              id={`${line.key}-term`}
              className={SELECT_CLASSES}
              value={line.term_years}
              onChange={(event) => onPatch({ term_years: event.target.value })}
            >
              {/* Blank is a real choice, not a placeholder: it means "server decides". */}
              <option value="">1 year (default)</option>
              <option value="1">1 year</option>
              <option value="2">2 years</option>
            </select>
          </div>
          <div className="w-44">
            <TextField
              label="Starts on"
              type="date"
              value={line.term_start}
              error={dateError(line.term_start)}
              onChange={(event) => onPatch({ term_start: event.target.value })}
            />
          </div>
          <div className="w-44">
            <TextField
              label="Expires on"
              type="date"
              value={line.expiry_date}
              error={dateError(line.expiry_date)}
              onChange={(event) => onPatch({ expiry_date: event.target.value })}
            />
          </div>
          <p className="text-label text-fg-secondary max-w-xs pt-6">
            Leave either blank to let the term decide it. Change them freely if the renewal
            was agreed differently — what you type is what is stored.
          </p>
        </div>
      ) : null}

      {/*
       * CAPTURE, NOT ARITHMETIC. These appear for a subscription product because
       * "10 users, 12 months" is part of what was agreed and `quote_lines` has
       * columns for it. They are not multiplied by anything: the RATE field above
       * carries the agreed money, and §9.1's only inputs are qty, rate and
       * discount.
       */}
      {entry !== null && entry.subscription ? (
        <div className="flex flex-wrap gap-3">
          <div className="w-28">
            <TextField
              label="Users"
              inputMode="numeric"
              value={line.users}
              onChange={(event) => onPatch({ users: event.target.value })}
            />
          </div>
          <div className="w-28">
            <TextField
              label="Months"
              inputMode="numeric"
              value={line.months}
              onChange={(event) => onPatch({ months: event.target.value })}
            />
          </div>
        </div>
      ) : null}

    </div>
  );
}
